<template>
  <div class="provider-selection">
    <step-header
      v-if="showHeader"
      title="Select a provider"
      :subtitle="locationName"
    />
    <div class="body">
      <loading-status v-if="!providersError && (providersLoading || deciding)" />
      <div
        v-else-if="providersError"
        role="alert"
        data-cy="providers-error"
      >
        <pp-form-message
          type="error"
          text="Unable to load providers. Please refresh the page and try again."
        />
      </div>
      <div
        v-else
        class="select-docs"
      >
        <button
          v-for="provider in providers"
          :key="provider.providerId"
          type="button"
          class="provider-block"
          data-cy="provider-select"
          @click="onSelectProvider(provider)"
        >
          <pp-avatar-icon
            class="provider-pic"
            size="md"
            :name="providerFullName(provider)"
            :image="provider.photoUrl || DEFAULT_AVATAR"
          />
          <div class="provider-info">
            <div class="provider-name">
              {{ providerFullName(provider) }}
            </div>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import StepHeader from '@/components/StepHeader/StepHeader.vue'
import LoadingStatus from '@/components/LoadingStatus/LoadingStatus.vue'
import { formatProviderName } from '@/utils/provider'
import { trackView, trackClick } from '@/analytics'

const DEFAULT_AVATAR = 'https://ui-cdn.patientpop.com/assets/img/provider-avatar.png'

export default {
  name: 'ProviderSelection',
  components: { StepHeader, LoadingStatus },
  data () {
    return {
      DEFAULT_AVATAR,
      // Providers can finish loading before this screen decides to skip.
      // Keep the spinner up so the title does not flash on the way through.
      deciding: true
    }
  },
  computed: {
    ...mapGetters({
      locationName: 'reputation/locationName',
      selectedLocation: 'reputation/selectedLocation',
      selectedProvider: 'reputation/selectedProvider',
      providers: 'reputation/providers',
      providersLoading: 'reputation/providersLoading',
      providersError: 'reputation/providersError',
      analyticsDims: 'reputation/analyticsDims'
    }),
    showHeader () {
      return !this.deciding || Boolean(this.providersError)
    }
  },
  async created () {
    // A named provider was already chosen. Browser back should stay on this list
    // when the office has more than one provider.
    const isBootStub = this.selectedProvider && !this.selectedProvider.firstName && !this.selectedProvider.lastName
    const preselectedProviderId = isBootStub && this.selectedProvider.providerId
    const preselectedLegacyProviderId = isBootStub ? this.selectedProvider.legacyProviderId : null

    await this.loadProviders(this.selectedLocation.locationId)

    if (this.providersError) return

    if (isBootStub && (preselectedProviderId || preselectedLegacyProviderId != null)) {
      const match = preselectedProviderId
        ? this.providers.find(p => p.providerId === preselectedProviderId)
        : this.providers.find(p => String(p.legacyProviderId) === String(preselectedLegacyProviderId))
      if (match) {
        await this.selectProvider(match)
        this.$router.replace({ name: 'PatientDetails' })
        return
      }
      // A stale id with nobody to choose would leave an empty screen.
      if (this.providers.length > 0) {
        this.showList()
        return
      }
    }

    const chosenProviderId = this.selectedProvider && this.selectedProvider.providerId
    const chosenProviderLoaded = chosenProviderId && this.providers.some(p => p.providerId === chosenProviderId)
    if (chosenProviderId && this.providers.length > 0 && (this.providers.length > 1 || !chosenProviderLoaded)) {
      this.showList()
      return
    }

    if (this.providers.length === 0) {
      // The old screen records the view in initialize, then skips an empty list.
      trackView(this, 'provider_selection', this.analyticsDims)
      await this.selectProvider(null)
      this.$router.replace({ name: 'PatientDetails' })
      return
    }

    if (this.providers.length === 1) {
      trackView(this, 'provider_selection', this.analyticsDims)
      await this.selectProvider(this.providers[0])
      this.$router.replace({ name: 'PatientDetails' })
      return
    }

    this.showList()
  },
  methods: {
    ...mapActions({
      loadProviders: 'reputation/loadProviders',
      selectProvider: 'reputation/selectProvider',
      setProvidersError: 'reputation/setProvidersError'
    }),
    showList () {
      this.deciding = false
      trackView(this, 'provider_selection', this.analyticsDims)
    },
    providerFullName (provider) {
      return formatProviderName(provider)
    },
    async onSelectProvider (provider) {
      trackClick(this, formatProviderName(provider), this.analyticsDims)
      await this.selectProvider(provider)
      this.$router.push({ name: 'PatientDetails' })
    }
  }
}
</script>

<style lang="scss" scoped>
.select-docs {
  display: inline-block;
  text-align: center;
  width: 100%;
}

.provider-block {
  align-items: center;
  background: $surface-muted;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: inline-flex;
  height: 60px;
  margin: 7px;
  padding: 7px;
  text-align: left;
  vertical-align: top;
  width: 48%;

  &:hover {
    background: #eff2f4;
  }

  &:focus {
    box-shadow: 0 0 10px var(--color-primary, #555f7e);
    outline: none;
  }

  @include mobile {
    display: block;
    margin: 7px 0;
    width: 100%;
  }
}

.provider-pic {
  flex-shrink: 0;
  margin: 0 6px;
}

.provider-info {
  flex: 1;
  min-width: 0;
  padding-left: 10px;

  .provider-name {
    color: $text-primary;
    font-family: $font-family-base;
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
